import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, Mail, Inbox, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Login = () => (
  <div className="min-h-screen bg-cream flex items-center justify-center p-6">
    <div className="bg-white rounded-[32px] border-4 border-ink shadow-pop p-10 max-w-md w-full" data-testid="admin-login">
      <p className="font-display text-ink text-[32px] leading-tight">WPA enquiry inbox<span className="text-pink">.</span></p>
      <p className="mt-3 text-ink/70">Private to the WPA team. Sign in with the WPA Google account.</p>
      <button onClick={login} data-testid="admin-login-btn"
        className="mt-8 w-full bg-ink text-white py-4 rounded-full font-semibold hover:bg-pink transition-colors">
        Sign in with Google
      </button>
      <a href="/" className="mt-5 inline-flex items-center gap-2 text-[14px] text-ink/60 hover:text-pink" data-testid="admin-back-home">
        <ArrowLeft size={14} /> Back to website
      </a>
    </div>
  </div>
);

const STATUSES = [
  ["new", "New", "bg-sky"],
  ["contacted", "Contacted", "bg-lime"],
  ["deal_closed", "Deal Closed", "bg-pink text-white"],
];

const StatusTags = ({ e, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid={`admin-status-${e.id}`}>
    {STATUSES.map(([v, l, c]) => {
      const active = (e.status || "new") === v;
      return (
        <button key={v} onClick={() => !active && onChange(e.id, v)} data-testid={`admin-status-${e.id}-${v}`}
          aria-pressed={active}
          className={`rounded-full border-2 border-ink px-3 py-1 text-[12px] font-bold transition-colors ${active ? c : "bg-white text-ink/70 hover:text-ink"}`}>
          {l}
        </button>
      );
    })}
  </div>
);

const EnquiryCard = ({ e, onStatus }) => (
  <div className="bg-white rounded-[24px] border-2 border-ink p-6 flex flex-col gap-3" data-testid={`admin-enquiry-${e.id}`}>
    <StatusTags e={e} onChange={onStatus} />
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-ink text-[22px] leading-tight">{e.company}</p>
        <p className="text-ink/70 text-[14px]">{e.contact_name}</p>
      </div>
      <span className="bg-lime border-2 border-ink rounded-full px-3 py-1 text-[12px] font-bold">{e.budget}</span>
    </div>
    <p className="text-ink/80 text-[15px] leading-relaxed whitespace-pre-line">{e.goals}</p>
    <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-ink/10 text-[13px]">
      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 font-semibold text-pink hover:underline">
        <Mail size={14} /> {e.email}
      </a>
      <span className="text-ink/50">{new Date(e.created_at).toLocaleString()}</span>
    </div>
  </div>
);

export default function AdminInbox() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const setStatus = async (id, status) => {
    const prev = items;
    setItems(items.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await axios.patch(`${API}/admin/enquiries/${id}/status`, { status }, { withCredentials: true });
    } catch {
      setItems(prev);
      toast.error("Couldn't update the status, please try again");
    }
  };
  const count = (v) => items?.filter((x) => (x.status || "new") === v).length ?? 0;
  const shown = items?.filter((x) => filter === "all" || (x.status || "new") === filter);

  useEffect(() => {
    if (authed) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); setAuthed(true); })
      .catch(() => setAuthed(false));
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/admin/enquiries`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => setAuthed(false));
  }, [authed]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuthed(false);
  };

  if (authed === null) return <div className="min-h-screen bg-cream" />;
  if (!authed) return <Login />;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-inbox">
      <Toaster position="top-center" />
      <header className="flex items-center justify-between px-6 sm:px-10 py-5 bg-white border-b border-ink/10">
        <a href="/" className="font-display text-ink text-[22px]">Wanderpreneur<span className="text-pink">.</span></a>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-[13px] text-ink/60" data-testid="admin-user-email">{user?.email}</span>
          <button onClick={logout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border-2 border-ink px-4 py-2 rounded-full text-[13px] font-semibold hover:bg-ink hover:text-white transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-6 sm:px-10 py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h1 className="font-display text-ink leading-none text-4xl sm:text-5xl">Brand enquiries</h1>
          <span className="bg-pink text-white border-2 border-ink rounded-full px-4 py-1.5 text-[13px] font-bold" data-testid="admin-enquiry-count">
            {items ? items.length : "…"} total
          </span>
        </div>
        {count("new") > 0 && (
          <button onClick={() => setFilter("new")} data-testid="admin-new-badge"
            className="mb-6 w-full flex items-center justify-between gap-3 bg-sky border-2 border-ink rounded-[20px] px-5 py-4 text-left hover:bg-lime transition-colors">
            <span className="flex items-center gap-3 font-display text-ink text-[18px] sm:text-[20px]">
              <span className="min-w-9 h-9 px-2 rounded-full bg-pink text-white border-2 border-ink flex items-center justify-center text-[15px]" data-testid="admin-new-count">
                {count("new")}
              </span>
              new {count("new") === 1 ? "enquiry" : "enquiries"} waiting
            </span>
            <span className="text-[13px] font-semibold text-ink/80">View new →</span>
          </button>
        )}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filters">
          {[["all", `All (${items?.length ?? 0})`], ...STATUSES.map(([v, l]) => [v, `${l} (${count(v)})`])].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`admin-filter-${v}`}
              className={`rounded-full border-2 border-ink px-4 py-1.5 text-[13px] font-semibold transition-colors ${filter === v ? "bg-ink text-white" : "bg-white text-ink hover:bg-ink/5"}`}>
              {l}
            </button>
          ))}
        </div>
        {items && items.length === 0 && (
          <div className="bg-white rounded-[24px] border-2 border-ink p-10 text-center text-ink/70" data-testid="admin-empty">
            <Inbox className="mx-auto mb-3" /> No brand enquiries yet.
          </div>
        )}
        {items && items.length > 0 && shown.length === 0 && (
          <div className="bg-white rounded-[24px] border-2 border-ink p-10 text-center text-ink/70" data-testid="admin-filter-empty">
            No enquiries with this status.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {shown?.map((e) => <EnquiryCard key={e.id} e={e} onStatus={setStatus} />)}
        </div>
      </main>
    </div>
  );
}
